import { useRef, useState } from 'react'
import { AlertTriangle, Paperclip, X } from 'lucide-react'
import { ATTACHMENT_ACCEPTED_EXTENSIONS } from '@/api/attachments'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AttachmentChip } from './AttachmentChip'
import { AttachmentPreviewDialog } from './AttachmentPreviewDialog'
import type { LocalAttachment } from './types'

/**
 * The visible attachment-management surface for chat: the chip rail above
 * the input, the file-picker / drop overlay binding, and the preview
 * dialog. Designed so the surrounding chat composer can keep its own
 * textarea/send-button setup — this component renders ONLY the
 * attachment-related affordances and a wrapper that intercepts drag/drop
 * + paste events on a parent element.
 *
 * The drag overlay is positioned absolutely against `dropTarget`, which
 * is expected to be the chat composer element. Wiring it through props
 * (rather than a context) keeps the composer's responsibility narrow.
 */
export interface AttachmentComposerProps {
  attachments: readonly LocalAttachment[]
  uploading: boolean
  lastValidationError: string | null
  onAddFiles: (
    files: readonly File[],
    options?: { source?: 'paste' | 'screenshot' | 'file' },
  ) => void
  onRemove: (localId: string) => void
  onRetry: (localId: string) => void
  onClearValidationError: () => void
  disabled?: boolean
}

const ACCEPT = ATTACHMENT_ACCEPTED_EXTENSIONS.join(',')

export function AttachmentComposer({
  attachments,
  uploading,
  lastValidationError,
  onAddFiles,
  onRemove,
  onRetry,
  onClearValidationError,
  disabled,
}: AttachmentComposerProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)

  const activeAttachment =
    attachments.find((a) => a.localId === activeId) ?? null

  const onPick = () => {
    if (disabled) return
    inputRef.current?.click()
  }
  const onPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length > 0) onAddFiles(files, { source: 'file' })
    e.target.value = ''
  }

  return (
    <div className="space-y-2" data-testid="attachment-composer">
      <AttachmentRail
        attachments={attachments}
        onRemove={onRemove}
        onRetry={onRetry}
        onPreview={setActiveId}
      />
      <ContextSummary
        attachments={attachments}
        uploading={uploading}
        onAddClick={onPick}
        disabled={disabled}
      />
      <ValidationBanner
        message={lastValidationError}
        onDismiss={onClearValidationError}
      />
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="sr-only"
        onChange={onPicked}
        data-testid="attachment-file-input"
      />
      <AttachmentPreviewDialog
        attachment={activeAttachment}
        open={activeAttachment !== null}
        onOpenChange={(next) => {
          if (!next) setActiveId(null)
        }}
      />
    </div>
  )
}

function AttachmentRail({
  attachments,
  onRemove,
  onRetry,
  onPreview,
}: {
  attachments: readonly LocalAttachment[]
  onRemove: (localId: string) => void
  onRetry: (localId: string) => void
  onPreview: (localId: string) => void
}) {
  if (attachments.length === 0) return null
  return (
    <ul
      data-testid="attachment-rail"
      className="flex flex-wrap gap-2"
      aria-label="Attached context items"
    >
      {attachments.map((a) => (
        <li key={a.localId}>
          <AttachmentChip
            attachment={a}
            onRemove={onRemove}
            onRetry={onRetry}
            onPreview={onPreview}
          />
        </li>
      ))}
    </ul>
  )
}

/**
 * "Context will be sent to AI" line. Always rendered so users learn the
 * surface; switches text when there are attachments to reinforce what's
 * actually going to be included.
 */
function ContextSummary({
  attachments,
  uploading,
  onAddClick,
  disabled,
}: {
  attachments: readonly LocalAttachment[]
  uploading: boolean
  onAddClick: () => void
  disabled?: boolean
}) {
  const readyCount = attachments.filter((a) => a.status === 'ready').length
  const erroredCount = attachments.filter((a) => a.status === 'error').length
  const hasAny = attachments.length > 0

  let summary: string
  if (uploading) {
    summary = 'Uploading attachments — they will be sent when ready.'
  } else if (hasAny && erroredCount > 0 && readyCount > 0) {
    summary = `${readyCount} ready, ${erroredCount} need attention before sending.`
  } else if (hasAny && erroredCount > 0) {
    summary = `${erroredCount} attachment${erroredCount === 1 ? '' : 's'} failed — retry or remove before sending.`
  } else if (hasAny) {
    summary = `${readyCount} attachment${readyCount === 1 ? '' : 's'} will be sent as context.`
  } else {
    summary = 'Attach screenshots, PDFs, or text files as additional context.'
  }

  return (
    <div
      className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground"
      data-testid="attachment-context-summary"
      data-has-attachments={hasAny ? 'true' : 'false'}
    >
      <span className="flex items-center gap-1.5">
        <Paperclip className="size-3" aria-hidden />
        <span data-testid="attachment-context-summary-text">{summary}</span>
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onAddClick}
        disabled={disabled}
        data-testid="attachment-add-button"
        className="-mr-2 h-7 px-2 text-[11px]"
      >
        <Paperclip className="size-3" aria-hidden />
        {hasAny ? 'Add more' : 'Attach files'}
      </Button>
    </div>
  )
}

function ValidationBanner({
  message,
  onDismiss,
}: {
  message: string | null
  onDismiss: () => void
}) {
  if (!message) return null
  return (
    <div
      role="alert"
      data-testid="attachment-validation-banner"
      className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive"
    >
      <AlertTriangle
        className="mt-0.5 size-3.5 shrink-0"
        aria-hidden
      />
      <span className="flex-1">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss attachment error"
        className="-mr-1 -mt-0.5 inline-flex size-5 items-center justify-center rounded text-destructive/80 hover:bg-destructive/10 hover:text-destructive"
      >
        <X className="size-3" aria-hidden />
      </button>
    </div>
  )
}

/**
 * The visible drop-zone overlay. Rendered inside the chat composer's
 * relative-positioned wrapper. Hidden by default; the caller flips
 * `visible` based on the dragging signal from `useAttachmentDropTarget`.
 */
export function AttachmentDropOverlay({
  visible,
}: {
  visible: boolean
}) {
  if (!visible) return null
  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary-soft/80 backdrop-blur-sm',
      )}
      data-testid="attachment-drop-overlay"
      aria-hidden
    >
      <div className="flex flex-col items-center gap-1 text-sm font-medium text-primary-soft-foreground">
        <Paperclip className="size-5" aria-hidden />
        Drop to attach as chat context
      </div>
    </div>
  )
}
